import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const sessionId = searchParams.get('session_id');

  // Function to poll payment status
  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      setPaymentStatus('timeout');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stripe/status/${sessionId}`);
      
      if (!response.ok) {
        throw new Error('Failed to check payment status');
      }

      const data = await response.json();
      
      if (data.payment_status === 'paid') {
        setPaymentStatus('success');
        setPaymentData(data);
        return;
      } else if (data.status === 'expired') {
        setPaymentStatus('expired');
        setPaymentData(data);
        return;
      }

      // If payment is still pending, continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPaymentStatus('error');
    }
  };

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setPaymentStatus('no-session');
    }
  }, [sessionId]);

  const renderContent = () => {
    switch (paymentStatus) {
      case 'checking':
        return (
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mb-6"></div>
            <h2 className="text-2xl font-bold text-white mb-4">Verificando pago...</h2>
            <p className="text-gray-300">Por favor, espera mientras confirmamos tu pago.</p>
          </div>
        );

      case 'success':
        return (
          <div className="text-center">
            <div className="text-6xl mb-6">✅</div>
            <h2 className="text-4xl font-bold text-white mb-6">¡Pago Exitoso!</h2>
            <div className="bg-green-900/30 border border-green-500/50 text-green-300 p-6 rounded-xl mb-8">
              <h3 className="text-xl font-bold mb-4">Detalles de tu compra:</h3>
              <div className="text-left space-y-2">
                <p><strong>Servicio:</strong> {paymentData?.metadata?.package_name || 'Servicio de asesoría'}</p>
                <p><strong>Importe:</strong> {paymentData?.amount_total ? `€${paymentData.amount_total}` : 'N/A'}</p>
                <p><strong>Estado:</strong> Pagado completamente</p>
              </div>
            </div>
            <p className="text-gray-300 mb-8">
              Gracias por confiar en nosotros. Te hemos enviado un correo de confirmación con todos los detalles.
            </p>
            <div className="space-y-4">
              <Link
                to="/contacto"
                className="inline-block bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-4 rounded-xl font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-300 transform hover:scale-105"
              >
                📞 Reserva tu sesión
              </Link>
              <div>
                <Link
                  to="/"
                  className="text-purple-400 hover:text-purple-300 transition-colors duration-300"
                >
                  ← Volver al inicio
                </Link>
              </div>
            </div>
          </div>
        );

      case 'expired':
        return (
          <div className="text-center">
            <div className="text-6xl mb-6">⏰</div>
            <h2 className="text-4xl font-bold text-white mb-6">Sesión Expirada</h2>
            <div className="bg-yellow-900/30 border border-yellow-500/50 text-yellow-300 p-6 rounded-xl mb-8">
              <p>Tu sesión de pago ha expirado. Por favor, intenta de nuevo.</p>
            </div>
            <div className="space-y-4">
              <Link
                to="/pricing"
                className="inline-block bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-4 rounded-xl font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-300 transform hover:scale-105"
              >
                Volver a precios
              </Link>
            </div>
          </div>
        );

      case 'error':
      case 'timeout':
        return (
          <div className="text-center">
            <div className="text-6xl mb-6">❌</div>
            <h2 className="text-4xl font-bold text-white mb-6">Error de Verificación</h2>
            <div className="bg-red-900/30 border border-red-500/50 text-red-300 p-6 rounded-xl mb-8">
              <p>No pudimos verificar el estado de tu pago. Por favor, revisa tu correo para la confirmación o contacta con nosotros.</p>
            </div>
            <div className="space-y-4">
              <Link
                to="/contacto"
                className="inline-block bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-4 rounded-xl font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-300 transform hover:scale-105"
              >
                Contactar Soporte
              </Link>
              <div>
                <Link
                  to="/pricing"
                  className="text-purple-400 hover:text-purple-300 transition-colors duration-300"
                >
                  ← Volver a precios
                </Link>
              </div>
            </div>
          </div>
        );

      case 'no-session':
        return (
          <div className="text-center">
            <div className="text-6xl mb-6">🔍</div>
            <h2 className="text-4xl font-bold text-white mb-6">Sesión no encontrada</h2>
            <div className="bg-gray-900/30 border border-gray-500/50 text-gray-300 p-6 rounded-xl mb-8">
              <p>No se encontró información de la sesión de pago.</p>
            </div>
            <div className="space-y-4">
              <Link
                to="/pricing"
                className="inline-block bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-4 rounded-xl font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-300 transform hover:scale-105"
              >
                Ver servicios
              </Link>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <div className="flex flex-col items-center justify-center min-h-screen px-4">
        <div className="max-w-2xl w-full bg-slate-800/30 backdrop-blur-sm rounded-3xl border border-purple-500/20 p-8 lg:p-12">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccessPage;